import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Clock, MessageSquare, ShieldCheck } from 'lucide-react';
import LegalPageLayout from '@/components/legal/LegalPageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoTile = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-3 p-4 rounded-md bg-[#F9F8F4] border border-[#EBE7E0]">
    <div className="w-9 h-9 rounded-md bg-white border border-[#EBE7E0] flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.75} />
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-[#14213D]">{title}</p>
      <p className="text-xs text-[#5A6577] leading-relaxed mt-0.5">{children}</p>
    </div>
  </div>
);

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in every field.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success('Message sent — we\u2019ll get back to you shortly.');
      setForm({ name: '', email: '', subject: '', message: '', website: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send message. Please email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalPageLayout title="Contact Us" lastUpdated="Reply within 1–2 business days">
      <p>
        Have a question about the curriculum, a subscription, or a broken link? Want to request a feature,
        report a typo, or just say hello? Use the form below and it will land in the maintainer&apos;s
        inbox — no ticketing bots, just a teacher.
      </p>

      <div className="not-prose grid grid-cols-1 sm:grid-cols-3 gap-3 my-8">
        <InfoTile icon={Mail} title="Email">
          <a href="mailto:support@atlashistory.org" className="text-[#14213D] hover:underline break-all" data-testid="contact-email-direct">
            support@atlashistory.org
          </a>
        </InfoTile>
        <InfoTile icon={Clock} title="Typical response">
          Within 1&ndash;2 business days (M&ndash;F). Weekends may run longer.
        </InfoTile>
        <InfoTile icon={ShieldCheck} title="Privacy">
          Your message is emailed directly to the maintainer &mdash; no third-party ticketing.
        </InfoTile>
      </div>

      <div className="not-prose bg-white border border-[#EBE7E0] rounded-lg p-6 sm:p-8 shadow-sm my-6">
        {sent ? (
          <div className="text-center py-8" data-testid="contact-sent">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
              <Send className="h-5 w-5 text-emerald-600" strokeWidth={1.75} />
            </div>
            <h3 className="font-serif-display text-2xl text-[#14213D] mb-2">Message received</h3>
            <p className="text-sm text-[#5A6577] max-w-md mx-auto">
              Thanks for reaching out. You&apos;ll hear back within 1&ndash;2 business days at the email
              address you provided.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-6"
              onClick={() => setSent(false)}
              data-testid="contact-send-another-btn"
            >
              Send another message
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
            <div className="flex items-center gap-2 text-[#14213D]">
              <MessageSquare className="h-4 w-4" />
              <p className="text-sm font-semibold uppercase tracking-wider">Send a message</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="contact-name" className="text-xs uppercase tracking-wider text-[#5A6577]">Your name</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Ms. Rivera"
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-email" className="text-xs uppercase tracking-wider text-[#5A6577]">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@school.org"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-subject" className="text-xs uppercase tracking-wider text-[#5A6577]">Subject</Label>
              <Input
                id="contact-subject"
                data-testid="contact-subject-input"
                value={form.subject}
                onChange={update('subject')}
                placeholder="Question about Unit 4 primary sources"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-message" className="text-xs uppercase tracking-wider text-[#5A6577]">Message</Label>
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={update('message')}
                rows={6}
                placeholder="Tell us what's on your mind…"
                required
              />
              <p className="text-[11px] text-[#5A6577]">{form.message.length} / 5000 characters</p>
            </div>

            {/* Honeypot — hidden from real users, catches bots */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={update('website')}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-xs text-[#5A6577]">
                By sending, you agree to our{' '}
                <a href="/privacy" className="text-[#14213D] hover:underline">Privacy Policy</a>.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] w-full sm:w-auto"
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending&hellip;</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send message</>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>

      <h2 className="font-serif-display text-2xl text-[#14213D] mt-10 mb-3">Common questions</h2>
      <p><strong>How do I upgrade or cancel my subscription?</strong> Sign in, open{' '}
        <em>Subscription</em> from the sidebar, and manage it via Stripe&apos;s billing portal.
      </p>
      <p><strong>My district needs an invoice / W-9.</strong> Send us a note using the form above and
        we&apos;ll issue an itemized invoice within 1&ndash;2 business days.</p>
      <p><strong>I found a broken link or content error.</strong> Please include the unit and a link
        or screenshot &mdash; our audit dashboard verifies fixes within 24 hours.</p>
      <p><strong>Requesting a new feature.</strong> We read every request. Highlights that unlock a
        new classroom workflow get prioritized.</p>
    </LegalPageLayout>
  );
}
